import { supabase } from '../lib/supabase';

// Derive plan type from plan name string
const derivePlanType = (planName = '') => {
  const name = planName.toLowerCase();
  if (name.includes('glp') || name.includes('semaglutide') || name.includes('tirzepatide')) return 'GLP1';
  if (name.includes('medical')) return 'MEDICAL';
  return 'CORE';
};

// Map snake_case weekly_check_ins row → camelCase object expected by components
const mapCheckIn = (ci) => ({
  id: ci.id,
  weekNumber: ci.week_number,
  weight: ci.weight,
  waistMeasurement: ci.waist_measurement,
  hipMeasurement: ci.hip_measurement,
  chestMeasurement: ci.chest_measurement,
  sleepHours: ci.sleep_hours,
  waterIntake: ci.water_intake,
  exerciseDone: ci.exercise_done,
  challengesFaced: ci.challenges_faced,
  goalsNextWeek: ci.goals_next_week,
  overallMood: ci.overall_mood,
  notes: ci.additional_notes,
  // Medical fields
  medicationTaken: ci.medication_taken ?? null,
  missedDoses: ci.missed_doses,
  currentDosage: ci.current_dosage,
  injectionSite: ci.injection_site,
  bloodPressureSystolic: ci.blood_pressure_systolic,
  bloodPressureDiastolic: ci.blood_pressure_diastolic,
  bloodSugarFasting: ci.blood_sugar_fasting,
  bloodSugarPostMeal: ci.blood_sugar_post_meal,
  heartRate: ci.heart_rate,
  sideEffects: ci.side_effects,
  sideEffectsSeverity: ci.side_effects_severity,
  sideEffectsNotes: ci.side_effects_notes,
  appetiteLevel: ci.appetite_level,
  energyLevel: ci.energy_level,
  medicalConcerns: ci.medical_concerns,
  submittedAt: ci.submitted_at,
});

export const doctorAPI = {
  // Get current doctor profile (joined with doctors table)
  getProfile: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data: profile } = await supabase
      .from('profiles').select('*').eq('id', user.id).single();
    const { data: doctorInfo } = await supabase
      .from('doctors').select('*').eq('user_id', user.id).single();
    return { ...profile, doctorInfo };
  },

  // Get assigned patients (via doctor_client_assignments)
  getClients: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: doctor } = await supabase
      .from('doctors').select('id').eq('user_id', user.id).single();
    if (!doctor) return [];

    const { data: assignments, error } = await supabase
      .from('doctor_client_assignments')
      .select('client_id, assigned_at, status')
      .eq('doctor_id', doctor.id)
      .eq('status', 'ACTIVE');
    if (error) throw error;

    const clientIds = (assignments ?? []).map((a) => a.client_id);
    if (clientIds.length === 0) return [];

    const { data: clients } = await supabase
      .from('profiles').select('*').in('id', clientIds);
    return clients ?? [];
  },

  // Get clients with computed progress fields for dashboard cards
  getClientsWithProgress: async () => {
    const clients = await doctorAPI.getClients();

    return Promise.all(
      clients.map(async (client) => {
        const [latestResult, oldestResult, countResult, planResult] = await Promise.all([
          supabase
            .from('weekly_check_ins')
            .select('weight, overall_mood, submitted_at, week_number, blood_pressure_systolic, blood_pressure_diastolic, medication_taken, energy_level')
            .eq('user_id', client.id)
            .order('week_number', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('weekly_check_ins')
            .select('weight')
            .eq('user_id', client.id)
            .order('week_number', { ascending: true })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('weekly_check_ins')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', client.id),
          supabase
            .from('user_plans')
            .select('plan_name, current_week, start_date, end_date')
            .eq('user_id', client.id)
            .eq('status', 'ACTIVE')
            .maybeSingle(),
        ]);

        const latestCheckIn = latestResult.data;
        const oldestCheckIn = oldestResult.data;
        const activePlan = planResult.data;
        const checkInCount = countResult.count ?? 0;

        const latestWeight = latestCheckIn?.weight ?? null;
        const oldestWeight = oldestCheckIn?.weight ?? null;
        const weightChange =
          latestWeight !== null && oldestWeight !== null && latestWeight !== oldestWeight
            ? parseFloat((latestWeight - oldestWeight).toFixed(1))
            : null;
        const daysSince = latestCheckIn?.submitted_at
          ? Math.floor((Date.now() - new Date(latestCheckIn.submitted_at)) / (1000 * 60 * 60 * 24))
          : null;

        return {
          id: client.id,
          email: client.email,
          firstName: client.first_name || '',
          lastName: client.last_name || '',
          phone: client.phone || '',
          planType: activePlan ? derivePlanType(activePlan.plan_name) : null,
          planName: activePlan?.plan_name || null,
          currentWeek: activePlan?.current_week || 1,
          subscriptionStatus: activePlan ? 'ACTIVE' : 'INACTIVE',
          latestWeight,
          weightChange,
          latestMood: latestCheckIn?.overall_mood ?? null,
          daysSinceLastCheckIn: daysSince,
          lastCheckInDate: latestCheckIn?.submitted_at ?? null,
          totalCheckIns: checkInCount,
          totalPhotos: 0,
        };
      })
    );
  },

  // Get full patient progress for the detail page
  getClientProgress: async (clientId) => {
    const [profileResult, planResult, checkInsResult, healthProfileResult] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', clientId).single(),
      supabase
        .from('user_plans')
        .select('plan_name, current_week, start_date, end_date, status')
        .eq('user_id', clientId)
        .eq('status', 'ACTIVE')
        .maybeSingle(),
      supabase
        .from('weekly_check_ins')
        .select('*')
        .eq('user_id', clientId)
        .order('week_number', { ascending: false }),
      supabase.from('health_profiles').select('*').eq('user_id', clientId).maybeSingle(),
    ]);

    const profile = profileResult.data;
    const plan = planResult.data;
    const rawCheckIns = checkInsResult.data ?? [];

    // Compute stats
    const weights = rawCheckIns.map((c) => c.weight).filter(Boolean);
    const currentWeight = weights.length > 0 ? weights[0] : null;
    const weightChange =
      weights.length >= 2
        ? parseFloat((weights[0] - weights[weights.length - 1]).toFixed(1))
        : null;
    const moodsWithValue = rawCheckIns.filter((c) => c.overall_mood);
    const averageMood =
      moodsWithValue.length > 0
        ? moodsWithValue.reduce((s, c) => s + c.overall_mood, 0) / moodsWithValue.length
        : null;

    // Weight trend for chart (oldest → newest)
    const weightTrend = rawCheckIns
      .filter((c) => c.weight)
      .map((c) => ({ weekNumber: c.week_number, weight: c.weight }))
      .reverse();

    // Check-in history with camelCase fields
    const checkInHistory = rawCheckIns.map((ci) => ({
      checkIn: mapCheckIn(ci),
      photos: [],
    }));

    return {
      id: profile?.id,
      email: profile?.email,
      firstName: profile?.first_name || '',
      lastName: profile?.last_name || '',
      phone: profile?.phone || '',
      planType: plan ? derivePlanType(plan.plan_name) : null,
      planName: plan?.plan_name || null,
      currentWeek: plan?.current_week || 1,
      subscriptionStartDate: plan?.start_date || null,
      subscriptionEndDate: plan?.end_date || null,
      healthProfile: healthProfileResult.data || null,
      totalCheckIns: rawCheckIns.length,
      totalPhotos: 0,
      currentWeight,
      weightChange,
      averageMood,
      weightTrend,
      checkInHistory,
      allPhotos: [],
    };
  },

  getClientCheckIns: async (clientId) => {
    const { data, error } = await supabase
      .from('weekly_check_ins').select('*').eq('user_id', clientId)
      .order('week_number', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  // Photos not implemented — storage feature deferred
  getClientPhotos: async () => [],
};

export default doctorAPI;
